import { useMemo, useState } from 'react';

import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { groupBy } from 'lodash';
import pluralize from 'pluralize';

import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useSeriesByTagSlug } from '@queries/TagQueries';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { ShowsTabContent } from '@screens/CommunityScreen/CommunityScreen';
import { ScheduledSocialPostTemplate } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';

dayjs.extend(advancedFormat);

// Use dayjs to get all the dates in the range formatted as YYYY-MM-DD
const getDatesInRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const end = dayjs(endDate);

  let currentDate = dayjs(startDate);

  while (currentDate.isBefore(end) || currentDate.isSame(end)) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
};

interface WorldPrideShowManagementScreenProps {
  tagSlug: string;
}

export const WorldPrideShowManagementScreen =
  createScreenComponent<WorldPrideShowManagementScreenProps>(
    ({ tagSlug = 'world-pride-drag', back }) => {
      const { isFetched: seriesIsReady, series, seriesHost } = useSeriesByTagSlug(tagSlug);
      const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
        {
          tagIds: [series?._id],
          startDate: dayjs().format('YYYY-MM-DD'),
          rsvpData: 'ignore',
        },
        seriesIsReady,
      );
      const [isSlidePreviewOpen, setIsSlidePreviewOpen] = useState(false);
      const { openGatedAddShowPopup } = useEntertainerEngagementContext();

      const handleAddEvent = () => {
        openGatedAddShowPopup({
          seriesId: series._id,
          cityId: series.city._id,
          isEntertainerOptional: true,
        });
      };

      const dateRange = useMemo(
        () =>
          series?.calendarPost?.startDate && series?.calendarPost?.endDate
            ? getDatesInRange(series?.calendarPost?.startDate, series?.calendarPost?.endDate)
            : [],
        [series?.calendarPost?.startDate, series?.calendarPost?.endDate],
      );

      const eventsByDate = useMemo(() => groupBy(events, 'startDate'), [events]);

      return (
        <Screen
          name="Series Events Screen"
          hideToolbar
          disableLocationPermissionGate
          headerProps={{
            back: { to: back ?? '/home' },
            title: <Screen.GagglTitle />,
          }}
        >
          <Screen.Content maxWidth={425}>
            {seriesIsReady && series && (
              <>
                <h1 style={{ fontSize: '1.8rem', margin: 0 }}>{`${series.title} Shows`}</h1>

                {isSlidePreviewOpen ? (
                  <>
                    <p className="mt-0">
                      To help you understand how we'll be sharing these events, here are the slides
                      that we will be posting to Instagram.
                    </p>

                    <HorizontalScroll
                      items={[
                        <div className="d-flex gap-1 pe-4">
                          <ScheduledSocialPostTemplate
                            cardTitle={'Slide 1'}
                            imageUrl={`${process.env.REACT_APP_API_URL}/public/assets/images/world-pride/drag-kings-cover-slide.png`}
                            eventTitle="World Pride Drag King Shows"
                            eventDetails={'--'}
                          />

                          {dateRange.map((date, i) => (
                            <ScheduledSocialPostTemplate
                              cardTitle={`Slide ${i + 2}`}
                              imageUrl={`${process.env.REACT_APP_API_URL}/v1/series/${series._id}/calendar/${date}/slide.jpg`}
                              eventTitle={dayjs(date).format('dddd, MMM Do')}
                              eventDetails={`${eventsByDate[date]?.length ?? 0} ${pluralize(
                                'show',
                                eventsByDate[date]?.length ?? 0,
                              )}`}
                            />
                          ))}
                        </div>,
                      ]}
                    />
                  </>
                ) : (
                  <div
                    className="d-flex justify-content-between align-items-center py-2 px-3"
                    onClick={() => setIsSlidePreviewOpen(true)}
                    style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
                  >
                    <div>
                      <strong>Preview Calendar Post for Instagram</strong>
                    </div>
                    <div>
                      <FontAwesomeIcon name="chevron-down" />
                    </div>
                  </div>
                )}

                <ShowsTabContent
                  hideOpenStages
                  tagIds={[series._id]}
                  onAddEvent={handleAddEvent}
                  back={`/noauth/world-pride/manage-shows`}
                />
              </>
            )}
          </Screen.Content>
        </Screen>
      );
    },
  );
